import { useState } from "react";
import { formatINR } from "@/lib/api";
import { SubTabs } from "@/components/stores/storesUi";

// Column headings per register, as the sheets name them.
const COLS = {
  sales: { ref: "Invoice", item: "Description", qty: "Qty", value: "Job value" },
  purchase: { ref: "Voucher", item: "Description", qty: "Qty", value: "Invoice value" },
  rmcost: { ref: "BPR", item: "Material", qty: "Used (sq m)", value: "Cost" },
};
const KEY_TEXT = {
  sales: "invoice number + product code + description",
  purchase: "voucher number + product code + description",
  rmcost: "BPR number + raw material code",
};
const FIELD_LABEL = {
  job_value: "Job value", taxable_value: "Taxable value", invoice_value: "Invoice value", unit_price: "Price",
  invoice_date: "Invoice date", party_name: "Party", party_code: "Party code", product_code: "Product code",
  po_number: "PO no.", po_date: "PO date", material_cost: "Value used", tax_rate: "GST rate", cgst: "CGST",
  sgst: "SGST", igst: "IGST", dev_charges: "Dev", consumption_sqm: "Consumption (sq m)",
};
const label = (f) => FIELD_LABEL[f] || f.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const num = (n) => (n || 0).toLocaleString("en-IN");
const fmt = (v) => (v === "" || v == null ? "—" : typeof v === "number" ? v.toLocaleString("en-IN") : String(v));

/**
 * What a sales / purchase / RM cost import will do (preview) or did (result). Lines are
 * added only when new; a line already in the ERP is never changed. `plan` is the
 * `finance` block returned by /api/import/* (backend/routes/finance_import.py).
 */
export default function RegisterImportPlan({ plan, done = false }) {
  const c = plan.counts;
  const cols = COLS[plan.kind];
  const lists = [
    { value: "new", label: `New lines · ${num(c.new)}`, n: c.new },
    { value: "differs", label: `Different in the file · ${num(c.differs)}`, n: c.differs },
    { value: "repeats", label: `Repeated lines · ${num(c.repeated_keys)}`, n: c.repeated_keys },
    { value: "skipped", label: `Skipped rows · ${num(c.skipped_rows)}`, n: c.skipped_rows },
    { value: "unread", label: `Cells not read · ${num(c.unread_cells)}`, n: c.unread_cells },
  ].filter((l) => l.n > 0);
  const [picked, setView] = useState(null);
  const view = lists.some((l) => l.value === picked) ? picked : lists[0]?.value;

  return (
    <div data-testid="register-import-plan">
      <div className="p-5 border-b border-neutral-200 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile label={done ? "Added" : "New lines"} value={num(c.new)} sub={formatINR(plan.value.new)} tone="text-emerald-700" testid="reg-new" />
        <Tile label="Already in the ERP" value={num(c.unchanged)} testid="reg-unchanged" />
        <Tile label="Different · ERP kept" value={num(c.differs)} tone={c.differs ? "text-amber-700" : ""} testid="reg-differs" />
        <Tile label="Rows skipped" value={num(c.skipped_rows)} testid="reg-skipped" />
      </div>

      <div className="px-5 py-3 border-b border-neutral-200 text-xs text-neutral-600 space-y-1.5">
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="label-caps text-[10px] mr-1">Columns</span>
          <span className="px-2 py-0.5 border border-neutral-200 rounded-sm bg-neutral-50">
            {plan.columns.read.length} read
          </span>
          {plan.columns.ignored.length > 0 && (
            <span className="text-neutral-400" title={plan.columns.ignored.join(", ")}>
              · not used: {plan.columns.ignored.join(", ")}
            </span>
          )}
        </div>
        <p>
          A line is known by its {KEY_TEXT[plan.kind]}, so importing the same file again, or a longer one, adds only the
          new lines. A line already in the ERP is never changed: if the file differs, it is listed and the ERP copy stays.
        </p>
      </div>

      <Links plan={plan} done={done} />

      {lists.length > 0 && (
        <div className="p-5 space-y-3">
          <SubTabs value={view} onChange={setView} options={lists} testid="reg-view" />
          {view === "new" && <LinesTable rows={plan.new} total={c.new} cols={cols} />}
          {view === "differs" && <DiffTable rows={plan.differs} total={c.differs} cols={cols} />}
          {view === "repeats" && <RepeatsTable rows={plan.repeats} total={c.repeated_keys} cols={cols} />}
          {view === "skipped" && <SkippedTable skipped={plan.skipped} total={c.skipped_rows} cols={cols} />}
          {view === "unread" && <UnreadTable rows={plan.unread} total={c.unread_cells} />}
        </div>
      )}
    </div>
  );
}

// Where the money lands: revenue / RM cost on jobs, for the whole register after this import.
function Links({ plan, done }) {
  const when = done ? "now" : "after this import";
  if (plan.kind === "sales" && plan.links.sales) {
    const s = plan.links.sales;
    return (
      <div className="px-5 py-3 border-b border-neutral-200 text-xs text-neutral-700 space-y-1.5" data-testid="reg-links">
        <div>
          Revenue on jobs {when}: <b className="font-mono-num">{formatINR(s.linked_value)}</b> of{" "}
          <span className="font-mono-num">{formatINR(s.value)}</span> in the sales register, across{" "}
          <b className="font-mono-num">{num(s.jobs)}</b> jobs.
        </div>
        <Chips label="Matched by" items={s.by_method} />
        <Chips label="Not on a job" items={s.unlinked} tone="text-amber-800" />
        {Object.keys(s.unlinked).length > 0 && (
          <p className="text-neutral-500">
            Unmatched lines still count in dashboard revenue; they join a job once its invoice number (or PO number and
            product code) is in the ERP.
          </p>
        )}
      </div>
    );
  }
  if (plan.kind === "rmcost" && plan.links.rmcost) {
    const r = plan.links.rmcost;
    return (
      <div className="px-5 py-3 border-b border-neutral-200 text-xs text-neutral-700" data-testid="reg-links">
        RM cost on jobs {when}: <b className="font-mono-num">{formatINR(r.linked_value)}</b> of{" "}
        <span className="font-mono-num">{formatINR(r.value)}</span> across <b className="font-mono-num">{num(r.jobs)}</b> jobs
        {r.bprs_not_in_erp > 0 && (
          <span className="text-amber-800"> · {num(r.bprs_not_in_erp)} BPRs not in the ERP ({formatINR(r.value_not_in_erp)})</span>
        )}
      </div>
    );
  }
  return null;
}

function Chips({ label: text, items, tone = "" }) {
  const entries = Object.entries(items || {});
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 items-center">
      <span className="label-caps text-[10px] mr-1">{text}</span>
      {entries.map(([how, v]) => (
        <span key={how} className={`px-2 py-0.5 border border-neutral-200 rounded-sm ${tone}`}>
          {how} · <span className="font-mono-num">{num(v.lines)}</span> lines · <span className="font-mono-num">{formatINR(v.value)}</span>
        </span>
      ))}
    </div>
  );
}

function Tile({ label: text, value, sub, tone = "", testid }) {
  return (
    <div data-testid={testid}>
      <div className="label-caps text-[10px]">{text}</div>
      <div className={`font-mono-num text-xl font-bold mt-1 ${tone || "text-neutral-900"}`}>{value}</div>
      {sub && <div className="text-[11px] text-neutral-500 font-mono-num">{sub}</div>}
    </div>
  );
}

const Head = ({ cols }) => (
  <thead className="bg-neutral-50 label-caps text-[10px]">
    <tr>{cols.map((c) => <th key={c} className="text-left px-3 py-2 whitespace-nowrap">{c}</th>)}</tr>
  </thead>
);

function Frame({ children, shown, total }) {
  return (
    <div className="border border-neutral-200">
      <div className="max-h-[28rem] overflow-auto">
        <table className="w-full text-sm">{children}</table>
      </div>
      {total > shown && (
        <div className="px-3 py-2 text-[11px] text-neutral-500 border-t border-neutral-100">First {shown} of {num(total)} shown.</div>
      )}
    </div>
  );
}

const LineCells = ({ r }) => (
  <>
    <td className="px-3 py-2 font-mono-num text-xs whitespace-nowrap">{r.ref}</td>
    <td className="px-3 py-2 text-xs whitespace-nowrap">{fmt(r.date)}</td>
    <td className="px-3 py-2 text-xs"><div className="truncate max-w-[11rem]" title={r.party}>{fmt(r.party)}</div></td>
    <td className="px-3 py-2 text-xs"><div className="truncate max-w-[16rem]" title={r.item}>{fmt(r.item)}</div></td>
  </>
);

function LinesTable({ rows, total, cols }) {
  return (
    <Frame shown={rows.length} total={total}>
      <Head cols={["Row", cols.ref, "Date", "Party", cols.item, cols.qty, cols.value]} />
      <tbody>
        {rows.map((r) => (
          <tr key={r.row} className="border-t border-neutral-100">
            <td className="px-3 py-2 font-mono-num text-xs text-neutral-500">{r.row}</td>
            <LineCells r={r} />
            <td className="px-3 py-2 font-mono-num text-xs text-right">{fmt(r.qty)}</td>
            <td className="px-3 py-2 font-mono-num text-xs text-right">{formatINR(r.value)}</td>
          </tr>
        ))}
      </tbody>
    </Frame>
  );
}

function DiffTable({ rows, total, cols }) {
  return (
    <>
      <p className="text-xs text-neutral-600">
        These lines are already in the ERP with other values. Nothing was changed; correct them in the ERP if the file is right.
      </p>
      <Frame shown={rows.length} total={total}>
        <Head cols={["Row", cols.ref, "Date", "Party", cols.item, "In the ERP (kept) → in the file"]} />
        <tbody>
          {rows.map((r) => (
            <tr key={r.row} className="border-t border-neutral-100 align-top">
              <td className="px-3 py-2 font-mono-num text-xs text-neutral-500">{r.row}</td>
              <LineCells r={r} />
              <td className="px-3 py-2 text-xs space-y-0.5">
                {r.changes.map((x) => (
                  <div key={x.field}>
                    <span className="text-neutral-500">{label(x.field)}:</span>{" "}
                    <span className="font-medium text-neutral-900">{fmt(x.erp)}</span>{" → "}
                    <span className="text-neutral-500">{fmt(x.file)}</span>
                  </div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </Frame>
    </>
  );
}

function RepeatsTable({ rows, total, cols }) {
  return (
    <>
      <p className="text-xs text-neutral-600">
        The same {cols.ref.toLowerCase()} and item appear on more than one row. Each row is kept as its own line; rows that
        are identical in every column may be a copy-paste slip worth checking with accounts.
      </p>
      <Frame shown={rows.length} total={total}>
        <Head cols={["First row", cols.ref, "Date", "Party", cols.item, "Lines", ""]} />
        <tbody>
          {rows.map((r) => (
            <tr key={r.row} className="border-t border-neutral-100">
              <td className="px-3 py-2 font-mono-num text-xs text-neutral-500">{r.row}</td>
              <LineCells r={r} />
              <td className="px-3 py-2 font-mono-num text-xs text-right">{r.lines}</td>
              <td className="px-3 py-2 text-xs">{r.identical && <span className="text-amber-800">identical rows</span>}</td>
            </tr>
          ))}
        </tbody>
      </Frame>
    </>
  );
}

function SkippedTable({ skipped, total, cols }) {
  return (
    <>
      <div className="flex flex-wrap gap-2 text-xs">
        {Object.entries(skipped.by_reason).map(([reason, n]) => (
          <span key={reason} className="px-2 py-1 border border-neutral-200 rounded-sm">
            {reason} · <b className="font-mono-num">{num(n)}</b>
          </span>
        ))}
      </div>
      <p className="text-xs text-neutral-500">Blank lines and total rows have no {cols.ref.toLowerCase()} number and are never imported.</p>
      <Frame shown={skipped.rows.length} total={total}>
        <Head cols={["Row", `Value in the ${cols.ref} column`, "Reason"]} />
        <tbody>
          {skipped.rows.map((s) => (
            <tr key={s.row} className="border-t border-neutral-100">
              <td className="px-3 py-1.5 text-xs font-mono-num">{s.row}</td>
              <td className="px-3 py-1.5 text-xs font-mono-num">{fmt(s.value)}</td>
              <td className="px-3 py-1.5 text-xs text-neutral-600">{s.reason}</td>
            </tr>
          ))}
        </tbody>
      </Frame>
    </>
  );
}

function UnreadTable({ rows, total }) {
  return (
    <>
      <p className="text-xs text-neutral-500">
        These cells aren't a number or date their column expects. The line is still imported; a date is kept as written.
      </p>
      <Frame shown={rows.length} total={total}>
        <Head cols={["Row", "Column", "Cell"]} />
        <tbody>
          {rows.map((u, i) => (
            <tr key={`${u.row}-${u.column}-${i}`} className="border-t border-neutral-100">
              <td className="px-3 py-1.5 text-xs font-mono-num">{u.row}</td>
              <td className="px-3 py-1.5 text-xs">{u.column}</td>
              <td className="px-3 py-1.5 text-xs font-mono-num whitespace-pre-line">{fmt(u.value)}</td>
            </tr>
          ))}
        </tbody>
      </Frame>
    </>
  );
}
